<div class="side-by-side-correction-panel">
  <div class="side-by-side-correction-panel-header">
    <div class="title">
      <ui-button type="secondary" icon="close_icon" iconSize="large" (click)="close()"></ui-button>
      <div class="title-info">
        <div class="title-text">
          {{ 'quotations_and_orders.side_by_side_panel.title' | translate }} {{ quotation?.name }}
        </div>
        <app-order-header-tags
          [quotation]="quotation"
          [segregationCheck]="segregationCheck"
          [splitBlocked]="isSplitPreviewBlocked"
          [ready]="!dataLoading && !loadError"
          (splitClick)="openSplitPreview()"
          (duplicateDismissed)="dismissDuplicateBadge()">
        </app-order-header-tags>
        <div class="title-actions" *ngIf="!dataLoading && !loadError">
          <ui-copyable
            size="big"
            [value]="quotation?.name"
            [label]="'quotations_and_orders.volta_id' | translate">
          </ui-copyable>
          <a
            class="title-action-btn"
            [routerLink]="['/quotations', quotation?.id]"
            target="_blank"
            [matTooltip]="'quotations_and_orders.side_by_side_panel.open_in_new_tab' | translate"
            matTooltipPosition="below">
            <ui-svg-icon svgIcon="open-link" size="small"></ui-svg-icon>
          </a>
        </div>
      </div>
    </div>
    <!--
      WB-7478: from Smart Inbox the panel is an approval surface — Approve Order is
      primary, Save becomes secondary, and an overflow menu carries the rest. From
      the order-details page only Save shows, exactly as before. The overflow menu
      stays available on a read-only record (view / duplicate / download are all
      still valid there); only the mutating items drop out.
    -->
    <div class="actions" *ngIf="!dataLoading && !loadError">
      <!--
        Unsaved-changes affordance. Rendered outside the origin branch below so both
        the Smart Inbox and order-details surfaces get it, and it explains why
        Approve Order is disabled. A read-only record can't be dirty.
      -->
      <span
        class="unsaved-indicator"
        *ngIf="!viewMode && isDirty"
        [matTooltip]="'quotations_and_orders.side_by_side_panel.unsaved_changes_tooltip' | translate"
        matTooltipClass="product-status-tooltip"
      >
        <ui-svg-icon svgIcon="alert-circle" size="small"></ui-svg-icon>
        <span>{{ 'quotations_and_orders.side_by_side_panel.unsaved_changes' | translate }}</span>
      </span>

      <ng-container *ngIf="isSmartInboxOrigin; else saveOnlyActions">
        <ui-button
          *ngIf="!viewMode"
          type="secondary"
          [label]="('quotations_and_orders.side_by_side_panel.save_changes' | translate | uppercase)"
          (click)="save()">
        </ui-button>
        <ng-container *appHasPermission="{ module: 'order', action: 'approve' }">
          <ui-button
            *ngIf="canApproveOrder"
            type="primary"
            [label]="('quotations_and_orders.approve_order' | translate | uppercase)"
            [disabled]="isApproveBlocked"
            (click)="approveOrder()">
          </ui-button>
        </ng-container>
        <ui-button
          type="secondary"
          icon="more_vert"
          iconSize="large"
          [matMenuTriggerFor]="panelActionsMenu">
        </ui-button>
      </ng-container>

      <ng-template #saveOnlyActions>
        <!-- Same label as the Smart Inbox action for consistency across origins. -->
        <ui-button
          *ngIf="!viewMode"
          type="primary"
          [label]="('quotations_and_orders.side_by_side_panel.save_changes' | translate | uppercase)"
          (click)="save()">
        </ui-button>
        <!--
          Same trigger as Smart Inbox, but the menu renders a reduced set from this
          entry point — see `panelActionsMenu`.
        -->
        <ui-button
          type="secondary"
          icon="more_vert"
          iconSize="large"
          [matMenuTriggerFor]="panelActionsMenu">
        </ui-button>
      </ng-template>
    </div>
  </div>

  <!--
    Overflow menu, shared by both entry points. The quick-actions row toggle sits
    first, above a divider; the destructive trio (cancel / archive / delete) is
    separated by a further divider per the design.
    
    From the order-details page the menu is deliberately reduced to just the toggle
    and Delete: the other actions all already exist on that page itself, so
    surfacing them here would only duplicate them. Everything between the first
    divider and Delete is therefore Smart-Inbox-only.
  -->
  <mat-menu #panelActionsMenu="matMenu" class="panel-actions-menu">
    <!--
      The toggle is a styled span rather than a mat-slide-toggle: nesting an
      interactive control inside the mat-menu-item button is invalid markup and
      would fire the toggle twice. The whole row is the control.
    -->
    <button
      mat-menu-item
      class="menu_item menu_item--toggle"
      role="menuitemcheckbox"
      [attr.aria-checked]="quickActionsEnabled"
      (click)="$event.stopPropagation(); toggleQuickActionsRow()">
      <ui-svg-icon svgIcon="sliders-horizontal" size="small"></ui-svg-icon>
      <span>{{ 'quick_actions.title' | translate }}</span>
      <span class="menu_item_switch" [class.menu_item_switch--on]="quickActionsEnabled" aria-hidden="true">
        <span class="menu_item_switch_knob"></span>
      </span>
    </button>

    <mat-divider></mat-divider>

    <ng-container *ngIf="isSmartInboxOrigin">
      <button mat-menu-item class="menu_item" (click)="viewFullDetails()">
        <ui-svg-icon svgIcon="open-link" size="small"></ui-svg-icon>
        <span>{{ 'quotations_and_orders.side_by_side_panel.actions.view_full_details' | translate }}</span>
      </button>
      <button mat-menu-item class="menu_item" (click)="duplicateOrder()">
        <ui-svg-icon svgIcon="copy" size="small"></ui-svg-icon>
        <span>{{ 'quotations_and_orders.actions.duplicate' | translate }}</span>
      </button>

      <mat-divider></mat-divider>

      <button mat-menu-item class="menu_item" (click)="downloadPdf()">
        <ui-svg-icon svgIcon="download_icon" size="small"></ui-svg-icon>
        <span>{{ 'quotations_and_orders.side_by_side_panel.actions.download_pdf' | translate }}</span>
      </button>
      <button mat-menu-item class="menu_item" (click)="downloadCsv()">
        <ui-svg-icon svgIcon="download_icon" size="small"></ui-svg-icon>
        <span>{{ 'quotations_and_orders.side_by_side_panel.actions.download_csv' | translate }}</span>
      </button>

      <ng-container *ngIf="!viewMode || canArchiveOrder">
        <mat-divider></mat-divider>

        <!-- X for cancel; the trashcan is reserved for delete. -->
        <button mat-menu-item class="menu_item" *ngIf="!viewMode" (click)="cancelOrder()">
          <ui-svg-icon svgIcon="close_icon" size="small"></ui-svg-icon>
          <span>{{ 'quotations_and_orders.side_by_side_panel.actions.cancel_order' | translate }}</span>
        </button>
        <button mat-menu-item class="menu_item" *ngIf="canArchiveOrder" (click)="archiveOrder()">
          <ui-svg-icon svgIcon="archive" size="small"></ui-svg-icon>
          <span>{{ 'quotations_and_orders.actions.archive' | translate }}</span>
        </button>
      </ng-container>
    </ng-container>

    <!-- Available from both entry points. -->
    <ng-container *appHasPermission="{ module: 'order', action: 'delete' }">
      <button
        mat-menu-item
        class="menu_item menu_item--danger"
        *ngIf="canDeleteOrder"
        (click)="deleteOrder()">
        <ui-svg-icon svgIcon="Trash" size="small"></ui-svg-icon>
        <span>{{ 'quotations_and_orders.actions.delete' | translate }}</span>
      </button>
    </ng-container>
  </mat-menu>

  <!-- Quick-actions row: available from both entry points, off by default. -->
  <app-order-quick-actions-row
    *ngIf="showQuickActionsRow"
    [quotation]="quotation"
    [fields]="quickActionFields"
    [readonly]="viewMode"
    [shippingDate]="detailsDraft.shippingDate"
    [poNumber]="detailsDraft.customerPoNumber"
    [quoteId]="detailsDraft.quoteId"
    [companyName]="detailsDraft.partnerCompanyName"
    [locationName]="detailsDraft.partnerName"
    [locationAddress]="detailsDraft.shippingAddress"
    (action)="onQuickAction($event)"
    (hide)="toggleQuickActionsRow()"
    (shippingDateChange)="onQuickActionsShippingDateChange($event)"
    (fieldsChange)="onQuickActionsFieldsChange($event)"
    (ownerChanged)="onQuickActionsOwnerChanged($event)">
  </app-order-quick-actions-row>
  <!-- Loading (spinner + rotating status text) -->
  <div *ngIf="dataLoading" class="side-by-side-correction-panel-loading">
    <ui-svg-icon svgIcon="refresh-cc" size="xlarge" class="side-by-side-correction-panel-loading__spinner"></ui-svg-icon>
    <span class="side-by-side-correction-panel-loading__message">{{ currentLoadingStatusKey | translate }}</span>
  </div>
  <!-- Load error -->
  <div *ngIf="loadError" class="side-by-side-correction-panel-loading side-by-side-correction-panel-loading--error">
    <span class="side-by-side-correction-panel-loading__message">{{ 'common.loading_order_details_error' | translate }}</span>
  </div>
  <!-- Content -->
  <div class="panels-content" *ngIf="!dataLoading && !loadError">
    <!-- Left Panel -->
    <div class="left-panel">
      <div class="tabs">
        <div class="navigation_tabs">
          <div
            class="tab"
            role="tab"
            [attr.aria-selected]="leftPanelActiveTab === 'products'"
            [class.active]="leftPanelActiveTab === 'products'"
            (click)="leftPanelActiveTab = 'products'"
            data-testid="products-tab"
          >
            {{ 'common.products' | translate }}
          </div>
          <div
            class="tab"
            role="tab"
            [attr.aria-selected]="leftPanelActiveTab === 'details'"
            [class.active]="leftPanelActiveTab === 'details'"
            (click)="leftPanelActiveTab = 'details'"
            data-testid="details-tab"
          >
            {{ 'quotations_and_orders.details' | translate }}
          </div>
        </div>

        <ng-container *ngIf="leftPanelActiveTab === 'products' && !productMatchEditMode">
          <div class="actions products-actions" *ngIf="!viewMode && selectedProductsCount > 0">
            <span>{{ 'quotations_and_orders.side_by_side_panel.items_selected' | translate: { count: selectedProductsCount } }}</span>
            <div class="products-actions-buttons">
              <ui-button type="secondary" icon="gift" (click)="markAsGift()"
                [matTooltip]="'quotations_and_orders.side_by_side_panel.mark_as_gift_tooltip' | translate" matTooltipClass="product-status-tooltip"></ui-button>
              <ui-button type="danger" icon="trash-cc" (click)="removeAllSelectedProducts()"
                [matTooltip]="'quotations_and_orders.side_by_side_panel.remove_selected_tooltip' | translate" matTooltipClass="product-status-tooltip"></ui-button>
            </div>
          </div>
          <!-- Configure columns -->
          <ui-table-column-settings
            triggerIcon="sliders-horizontal"
            [columns]="pickerColumns"
            (columnsChange)="onColumnsChange($event)"
          ></ui-table-column-settings>
        </ng-container>

        <!-- Same slot, same control as the products column picker above. -->
        <ui-detail-fields-settings
          *ngIf="leftPanelActiveTab === 'details' && !companyLocationEditMode"
          [groups]="fieldGroups"
          (groupChange)="onFieldGroupChange($event)"
        ></ui-detail-fields-settings>
      </div>
      <div class="content">
        <!-- Inline company/location editor — replaces only the details tab content; nav tabs stay visible -->
        <div class="company-location-overlay" *ngIf="companyLocationEditMode && leftPanelActiveTab === 'details'">
          <app-company-location-selector
            [company]="selectorCompany"
            [location]="selectorLocation"
            [initialStep]="selectorInitialStep"
            [prefillLocation]="selectorPrefill"
            [orderId]="quotation?.id"
            [canGoBack]="true"
            mode="edit"
            (completed)="onSelectorCompleted($event)"
            (cancelled)="onSelectorCancelled()">
          </app-company-location-selector>
        </div>
        <!-- Inline product matching editor — replaces only the products tab content; nav tabs stay visible -->
        <div class="product-match-overlay" *ngIf="productMatchEditMode && leftPanelActiveTab === 'products'">
          <app-matching-missing-products-selector
            [data]="matchSelectorData"
            (completed)="onMatchSelectorCompleted($event)"
            (cancelled)="onMatchSelectorCancelled()">
          </app-matching-missing-products-selector>
        </div>
        <div class="order-source-tab" *ngIf="leftPanelActiveTab === 'products' && !productMatchEditMode">
          <div class="products-content" *ngIf="products.length > 0 || unmatchedProducts.length > 0 || newProducts.length > 0">
              <table class="products-table">
              <thead>
                <tr class="row header" [style.grid-template-columns]="gridTemplateColumns" style="padding-top: 10px; height: unset">
                  <th *ngIf="!viewMode">
                    <mat-checkbox
                      class="zero-spacing-checkbox"
                      [checked]="isAllProductsSelected()"
                      [indeterminate]="isPartialSelectedAll()"
                      (change)="toggleAllProducts($event.checked)"
                    >
                    </mat-checkbox>
                  </th>
                  <!-- Product Name -->
                  <th class="sortable-column" *ngIf="isColumnVisible('product')" [style.order]="columnOrderIndex('product')">
                    <div style="display: flex; flex-direction: column">
                      <span>{{ 'quotations_and_orders.order_line_columns.product' | translate }}</span>
                      <span class="secondary-text">{{ productCode.primaryLabelKey | translate }}</span>
                    </div>
                  </th>

                  <th *ngIf="isColumnVisible('notes')" [style.order]="columnOrderIndex('notes')">
                    {{ 'quotations_and_orders.order_line_columns.notes' | translate }}
                  </th>

                  <th *ngIf="isColumnVisible('brand')" [style.order]="columnOrderIndex('brand')">
                    {{ 'quotations_and_orders.order_line_columns.brand' | translate }}
                  </th>

                  <th *ngIf="isColumnVisible('msrp')" [style.order]="columnOrderIndex('msrp')">
                    {{ 'quotations_and_orders.order_line_columns.msrp' | translate }}
                  </th>

                  <th *ngIf="isColumnVisible('cost')" [style.order]="columnOrderIndex('cost')">
                    {{ 'quotations_and_orders.order_line_columns.cost' | translate }}
                  </th>

                  <!-- unit price -->
                  <th *ngIf="isColumnVisible('unit_price')" [style.order]="columnOrderIndex('unit_price')">
                    {{ 'quotations_and_orders.order_line_columns.unit_price' | translate }}
                  </th>
                  <!-- Quantity -->
                  <th class="quantity-cell" *ngIf="isColumnVisible('quantity')" [style.order]="columnOrderIndex('quantity')">
                    <span class="col-header-with-info">
                      {{ 'quotations_and_orders.order_line_columns.quantity' | translate }}
                      <ui-svg-icon class="header-info-icon" svgIcon="info" size="small"
                        [matTooltip]="'quotations_and_orders.order_line_columns.quantity_tooltip' | translate"
                        matTooltipClass="qty-header-tooltip"
                        (click)="$event.stopPropagation()"></ui-svg-icon>
                    </span>
                  </th>
                  <!-- Preferred selling unit -->
                  <th *ngIf="isColumnVisible('quantity_preferred_selling_unit')" [style.order]="columnOrderIndex('quantity_preferred_selling_unit')">
                    <div class="col-header-two-line">
                      <span class="header-line header-line-primary col-header-with-info">
                        {{ 'quotations_and_orders.order_line_columns.quantity_preferred_selling_unit' | translate }}
                        <ui-svg-icon class="header-info-icon" svgIcon="info" size="small"
                          [matTooltip]="'quotations_and_orders.order_line_columns.quantity_preferred_selling_unit_tooltip' | translate"
                          matTooltipClass="qty-header-tooltip"
                          (click)="$event.stopPropagation()"></ui-svg-icon>
                      </span>
                      <span class="header-line header-line-secondary">{{ 'quotations_and_orders.order_line_columns.quantity_preferred_selling_unit_line2' | translate }}</span>
                    </div>
                  </th>
                  <!-- Units -->
                  <th *ngIf="isColumnVisible('quantity_units')" [style.order]="columnOrderIndex('quantity_units')">
                    <div class="col-header-two-line">
                      <span class="header-line header-line-primary col-header-with-info">
                        {{ 'quotations_and_orders.order_line_columns.quantity_units' | translate }}
                        <ui-svg-icon class="header-info-icon" svgIcon="info" size="small"
                          [matTooltip]="'quotations_and_orders.order_line_columns.quantity_units_tooltip' | translate"
                          matTooltipClass="qty-header-tooltip"
                          (click)="$event.stopPropagation()"></ui-svg-icon>
                      </span>
                      <span class="header-line header-line-secondary">{{ 'quotations_and_orders.order_line_columns.quantity_units_line2' | translate }}</span>
                    </div>
                  </th>
                  <!-- total price -->
                  <th *ngIf="isColumnVisible('total_amount')" [style.order]="columnOrderIndex('total_amount')">
                    {{ 'quotations_and_orders.order_line_columns.total_amount' | translate }}
                  </th>

                  <th *ngIf="isColumnVisible('profit_margin')" [style.order]="columnOrderIndex('profit_margin')">
                    {{ 'quotations_and_orders.order_line_columns.profit_margin' | translate }}
                  </th>

                  <th *ngIf="isColumnVisible('delivery_date')" [style.order]="columnOrderIndex('delivery_date')">
                    {{ 'quotations_and_orders.order_line_columns.delivery_date' | translate }}
                  </th>

                  <th *ngIf="isColumnVisible('stock')" [style.order]="columnOrderIndex('stock')">
                    {{ 'quotations_and_orders.order_line_columns.stock' | translate }}
                  </th>

                  <!-- Product custom field columns (WB-7619) — labels come resolved from the definition. -->
                  <th *ngFor="let key of visibleCustomFieldColumns" [style.order]="columnOrderIndex(key)">
                    {{ customFieldColumnLabel(key) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                  <div class="products-viewport-side-by-side"
                       [ngClass]="{'products-viewport-add-product': isAddProductMode}"
                       (scroll)="onViewportScroll($event)"
                       (wheel)="onUserScrollIntent()"
                       (touchstart)="onUserScrollIntent()"
                       (keydown)="onUserScrollIntent()">
                    <ng-template #standardProductRow let-product>
                      <tr class="row" [style.grid-template-columns]="gridTemplateColumns"
                          [class.row--active]="isRowActive(product)"
                          [attr.data-row-key]="rowKeyOf(product)"
                          (mouseenter)="onRowHover(product)" (mouseleave)="onRowHoverEnd()">
                        <td *ngIf="!viewMode">
                          <mat-checkbox
                            [checked]="isProductSelected(product)"
                            (change)="toggleProduct(product, $event.checked)"
                            (click)="$event.stopPropagation()"
                          ></mat-checkbox>
                        </td>
                        <!-- Product Name -->
                        <td class="product-name-col" *ngIf="isColumnVisible('product')" [style.order]="columnOrderIndex('product')">
                          <div class="product-name-column">
                            <div class="product_name">
                              <span [title]="product.name"
                                    (mouseenter)="onFieldHover(product, 'name')"
                                    (mouseleave)="onFieldHoverEnd()">
                                {{ product.name }}
                              </span>
                              <div class="product-state">
                                <span class="product-sku" [class.product-sku--matched]="isSkuMatchHighlight(product)"
                                      (mouseenter)="onFieldHover(product, 'code')"
                                      (mouseleave)="onFieldHoverEnd()">{{product | productCode}}</span>
                                <span *ngIf="getInlineMatchCode(product)" class="match-code-inline">({{ getInlineMatchCode(product) }})</span>
                                <ng-container *ngIf="getSemanticParsedCodes(product) as semanticCodes">
                                  <span class="semantic-code" *ngIf="semanticCodes.length">
                                    <ui-svg-icon svgIcon="ai-volta-icon" size="xsmall"></ui-svg-icon>
                                    <span class="semantic-code__value">{{ semanticCodes.join(', ') }}</span>
                                  </span>
                                </ng-container>
                                <ui-svg-icon *ngIf="canRollBack(product)" (click)="rollBackProduct(product)" svgIcon="rollback" size="medium" style="cursor: pointer;"></ui-svg-icon>
                              </div>
                            </div>
                            <div class="product-icons" [class.product-icons-view-mode]="viewMode">
                              <ui-svg-icon
                                class="status-icon"
                                [class]="getProductStatusIcon(product)"
                                [class.status-icon--actionable]="!viewMode && !product.added_manually"
                                (click)="!viewMode && !product.added_manually ? openMatchSingleOrderLineProduct(product, $event) : null; $event.stopPropagation()"
                                [svgIcon]="getProductStatusIcon(product)"
                                [style.cursor]="(!viewMode && !product.added_manually) ? 'pointer' : 'default'"
                                [matTooltip]="getProductMatchTooltip(product)"
                                matTooltipClass="product-status-tooltip"
                                size="medium">
                              </ui-svg-icon>
                            </div>
                          </div>
                        </td>
                        <td *ngIf="isColumnVisible('notes')" [style.order]="columnOrderIndex('notes')" (click)="$event.stopPropagation()">
                          <div class="notes-cell">
                            <!-- WITH NOTES: filled icon + centered interactive tooltip (note text + Edit) -->
                            <ui-svg-icon
                                *ngIf="product.notes; else addNoteBtn"
                                class="notes-icon has-notes"
                                tooltip=""
                                [tooltipTemplate]="notesTooltip"
                                [center]="true"
                                [maxWidth]="'320px'"
                                svgIcon="comment"
                                size="medium"
                                (click)="openLineNotesDialog(product)">
                            </ui-svg-icon>
                            <ng-template #notesTooltip>
                              <div class="notes-tooltip">
                                <div class="notes-tooltip-header">
                                    <span class="notes-tooltip-title">{{ 'quotations_and_orders.line_notes.column' | translate }}</span>
                                </div>
                                <div class="notes-tooltip-body">{{ product.notes }}</div>
                                <div class="notes-tooltip-actions">
                                    <button class="notes-tooltip-edit" (click)="$event.stopPropagation(); openLineNotesDialog(product)">
                                        <ui-svg-icon svgIcon="inline-edit" size="small"></ui-svg-icon>
                                        {{ 'quotations_and_orders.line_notes.edit' | translate }}
                                    </button>
                                </div>
                              </div>
                            </ng-template>

                            <!-- NO NOTES: comment icon that becomes a + button on hover, small actionless label -->
                            <ng-template #addNoteBtn>
                              <button
                                  type="button"
                                  class="notes-add-btn"
                                  tooltip=""
                                  [tooltipTemplate]="addNoteTooltip"
                                  [center]="true"
                                  (click)="openLineNotesDialog(product)">
                                <ui-svg-icon class="notes-icon notes-icon-default" svgIcon="comment" size="medium"></ui-svg-icon>
                                <ui-svg-icon class="notes-icon notes-icon-hover" svgIcon="plus-cc" size="medium"></ui-svg-icon>
                              </button>
                              <ng-template #addNoteTooltip>
                                <div class="notes-tooltip-mini">{{ 'quotations_and_orders.line_notes.add' | translate }}</div>
                              </ng-template>
                            </ng-template>
                          </div>
                        </td>
                        <td *ngIf="isColumnVisible('brand')" [style.order]="columnOrderIndex('brand')">
                          <span class="truncate" [title]="product.brand_name || ''">
                            {{ product.brand_name || '--' }}
                          </span>
                        </td>
                        <td [class.no-price]="product.price_type === 'none'" *ngIf="isColumnVisible('msrp')" [style.order]="columnOrderIndex('msrp')">
                          <ng-container *ngIf="product.price_type === 'none'; else msrpNotNone">{{ 'quotations_and_orders.price_source_none' | translate }}</ng-container>
                          <ng-template #msrpNotNone>
                            <ng-container *ngIf="product.price_unit !== null && product.price_unit !== undefined; else msrpEmpty">
                              {{ product.price_unit | number: '1.2-2' }} {{ quotationCurrency?.symbol }}
                            </ng-container>
                            <ng-template #msrpEmpty>--</ng-template>
                          </ng-template>
                        </td>
                        <td *ngIf="isColumnVisible('cost')" [style.order]="columnOrderIndex('cost')">
                          <ng-container *ngIf="product.cost !== null && product.cost !== undefined; else costEmpty">
                            {{ product.cost | number: '1.2-2' }} {{ quotationCurrency?.symbol }}
                          </ng-container>
                          <ng-template #costEmpty>--</ng-template>
                        </td>
                        <!-- unit price -->
                        <td class="price-cell" *ngIf="isColumnVisible('unit_price')" [style.order]="columnOrderIndex('unit_price')" (mouseenter)="onFieldHover(product, 'unit_price')" (mouseleave)="onFieldHoverEnd()">
                          <ng-container *ngIf="!product.is_gift; else isGiftTag">
                            <ng-container *ngIf="!viewMode && !quotation?.all_lines_no_price; else priceViewMode">
                              <!-- Edit mode: input + tooltip + badge -->
                              <div class="price-input-wrapper standard-input">
                                <input
                                  type="number"
                                  class="quantity-input"
                                  min="0"
                                  step="any"
                                  [value]="priceInputValue(product.final_price)"
                                  (input)="onPriceInput($event, product)"
                                  (click)="$event.stopPropagation()"
                                >

                                <ui-svg-icon *ngIf="product.price_type" tooltip=""
                                   [tooltipTemplate]="priceDetailsTpl"
                                   [offsetLeft]="200"
                                   [offsetTop]="-10"
                                   [maxWidth]="'350px'"
                                   svgIcon="info" size="medium" (click)="$event.stopPropagation()">
                                </ui-svg-icon>
                              </div>
                              <ng-template #priceDetailsTpl>
                                <app-price-details-tooltip
                                  [product]="product"
                                  [currencySymbol]="quotationCurrency?.symbol || ''"
                                  (applyPrice)="onApplyPriceFromTooltip(product, $event)">
                                </app-price-details-tooltip>
                              </ng-template>
                            </ng-container>
                            <ng-template #priceViewMode>
                              <div class="price-input-wrapper" [class.no-price]="product.price_type === 'none'">
                                {{ product.price_type === 'none' ? ('quotations_and_orders.price_source_none' | translate) : (product.final_price ? (product.final_price | number: '1.2-2') + ' ' + quotationCurrency?.symbol : '--') }}
                               <ui-svg-icon *ngIf="product.price_type" tooltip=""
                                   [tooltipTemplate]="priceDetailsViewTpl"
                                   [offsetLeft]="200"
                                   [offsetTop]="-10"
                                   [maxWidth]="'350px'"
                                   svgIcon="info" size="medium" (click)="$event.stopPropagation()">
                                </ui-svg-icon>
                              </div>
                              <ng-template #priceDetailsViewTpl>
                                <app-price-details-tooltip
                                  [product]="product"
                                  [currencySymbol]="quotationCurrency?.symbol || ''"
                                  [viewMode]="true">
                                </app-price-details-tooltip>
                              </ng-template>
                            </ng-template>
                          </ng-container>
                          <ng-template #isGiftTag>
                            <span>0.00 {{ quotationCurrency?.symbol }}</span>
                            <div class="free-badge">
                              <ui-svg-icon svgIcon="gift" size="medium"></ui-svg-icon>
                              {{ 'quotations_and_orders.side_by_side_panel.gift' | translate }}
                            </div>
                          </ng-template>
                        </td>
                        <!-- Quantity -->
                        <td class="quantity-cell" *ngIf="isColumnVisible('quantity')" [style.order]="columnOrderIndex('quantity')" (mouseenter)="onFieldHover(product, 'quantity')" (mouseleave)="onFieldHoverEnd()">
                          <ng-container *ngIf="!viewMode; else qtyDisplayMode">
                            <div class="quantity-badge standard-input">
                                <input
                                  type="number"
                                  class="quantity-input"
                                  min="0"
                                  step="any"
                                  [value]="product.quantity_handling_unit ?? 0"
                                  (input)="onHandlingUnitQtyInput($event, product)"
                                >
                                <mat-select
                                    class="hu-select"
                                    [class.hu-select-warning]="requiresHandlingUnitSelection(product)"
                                    panelClass="hu-select-panel"
                                    (selectionChange)="onHandlingUnitChange($event, product)"
                                    [value]="product.handling_unit_id"
                                    *ngIf="getHandlingUnits(product).length > 0"
                                    disableRipple
                                >
                                    <mat-select-trigger>
                                      <div class="hu-option-row" *ngIf="getSelectedHandlingUnit(product)">
                                          <span class="hu-option-name">{{ getSelectedHandlingUnit(product)?.name }}</span>
                                      </div>
                                    </mat-select-trigger>
                                    <mat-option *ngFor="let hu of getHandlingUnits(product)" [value]="hu.id">
                                        <div class="hu-option-row">
                                            <span class="hu-option-name">{{ hu.name }}</span>
                                            <span class="hu-option-quantity">{{ hu.quantity }} units</span>
                                        </div>
                                    </mat-option>
                                    <mat-option *appHasPermission="{ module: 'product', action: 'edit' }" class="hu-create-option" [value]="HU_CREATE_SENTINEL">
                                        <ui-svg-icon svgIcon="plus-cc" size="small"></ui-svg-icon>
                                        <span class="hu-create-label">{{ 'quotations_and_orders.side_by_side_panel.create_selling_unit' | translate }}</span>
                                    </mat-option>
                                </mat-select>
                                <ui-svg-icon tooltip="" [tooltipTemplate]="handlingUnitTooltipTpl" [offsetLeft]="250" [offsetTop]="-10" [svgIcon]="requiresHandlingUnitSelection(product) ? 'alert-circle' : 'info'" size="medium" (click)="$event.stopPropagation()"></ui-svg-icon>
                                <ng-template #handlingUnitTooltipTpl>
                                  <app-qty-details-tooltip
                                    [item]="product"
                                    [selectedHu]="getSelectedHandlingUnit(product)">
                                  </app-qty-details-tooltip>
                                </ng-template>
                            </div>
                          </ng-container>
                          <ng-template #qtyDisplayMode>
                            <div class="qty-display">
                              <span class="qty-value">{{ product.quantity_handling_unit ?? 0 }}</span>
                              <span class="hu-tag" *ngIf="getSelectedHandlingUnit(product)">
                                {{ getSelectedHandlingUnit(product)?.name }}
                              </span>
                              <ui-svg-icon tooltip="" [tooltipTemplate]="handlingUnitTooltipViewTpl" [offsetLeft]="250" [offsetTop]="-10" svgIcon="info" size="medium" (click)="$event.stopPropagation()"></ui-svg-icon>
                              <ng-template #handlingUnitTooltipViewTpl>
                                <app-qty-details-tooltip
                                  [item]="product"
                                  [selectedHu]="getSelectedHandlingUnit(product)">
                                </app-qty-details-tooltip>
                              </ng-template>
                            </div>
                          </ng-template>
                        </td>
                        <td *ngIf="isColumnVisible('quantity_preferred_selling_unit')" [style.order]="columnOrderIndex('quantity_preferred_selling_unit')">
                          <ng-container *ngIf="product.quantity_preferred_selling_unit !== null && product.quantity_preferred_selling_unit !== undefined; else noPreferredSellingUnitA">
                            {{ product.quantity_preferred_selling_unit }} {{ product.quantity_preferred_selling_unit_name }}
                          </ng-container>
                          <ng-template #noPreferredSellingUnitA>--</ng-template>
                        </td>
                        <td *ngIf="isColumnVisible('quantity_units')" [style.order]="columnOrderIndex('quantity_units')">
                          {{ product.quantity }} {{ 'quotations_and_orders.side_by_side_panel.units_suffix' | translate }}
                        </td>
                        <!-- total price -->
                        <td class="price-cell" [class.no-price]="!product.is_gift && product.price_type === 'none'" *ngIf="isColumnVisible('total_amount')" [style.order]="columnOrderIndex('total_amount')">
                          <ng-container *ngIf="!product.is_gift; else isGiftTag">
                            {{ product.price_type === 'none' ? ('quotations_and_orders.price_source_none' | translate) : (product.final_price ? (product.final_price! * product.quantity | number: '1.2-2') + ' ' + quotationCurrency?.symbol : '--') }}
                          </ng-container>
                          <ng-template #isGiftTag>
                            <span>0.00 {{ quotationCurrency?.symbol }}</span>
                          </ng-template>
                        </td>
                        <!-- Same computation as the order-details table (WB-7903). -->
                        <td class="profit-cell" *ngIf="isColumnVisible('profit_margin')" [style.order]="columnOrderIndex('profit_margin')">
                          <ng-container *ngIf="product.price_type === 'none'; else profitMarginValues">
                            <span class="profit-amount no-price">{{ 'quotations_and_orders.price_source_none' | translate }}</span>
                          </ng-container>
                          <ng-template #profitMarginValues>
                            <span class="profit-amount">{{ product.cost && product.final_price ? (((product.final_price - product.cost) * product.quantity) | number: '1.2-2') + ' ' + quotationCurrency?.symbol : '--' }}</span>
                            <span class="profit-markup">{{ product.cost && product.final_price ? (((product.final_price - product.cost) / product.final_price * 100) | number: '1.2-2') + '%' : '--' }}</span>
                          </ng-template>
                        </td>
                        <td *ngIf="isColumnVisible('delivery_date')" [style.order]="columnOrderIndex('delivery_date')">
                          <ng-container *ngIf="!viewMode; else deliveryDateDisplayMode">
                            <div class="standard-input has-icon">
                              <div class="input-container">
                                <input
                                    type="text"
                                    matInput
                                    [matDatepicker]="picker"
                                    class="standard-input-value"
                                    [placeholder]="'common.select_date' | translate"
                                    [value]="getDeliveryDateInputValue(product)"
                                    (dateChange)="onDeliveryDateChange($event, product)"
                                    (click)="picker.open()"
                                />
                                <button
                                    type="button"
                                    class="btn-icon"
                                    (click)="picker.open()"
                                    tabindex="-1"
                                    [attr.aria-label]="'quotations_and_orders.side_by_side_panel.open_calendar' | translate"
                                >
                                    <ui-svg-icon svgIcon="calendar-cc"></ui-svg-icon>
                                </button>
                                <mat-datepicker #picker></mat-datepicker>
                              </div>
                            </div>
                          </ng-container>
                          <ng-template #deliveryDateDisplayMode>
                            <span>{{product.delivery_date | date: DELIVERY_DATE_FORMAT}}</span>
                          </ng-template>
                        </td>
                        <td *ngIf="isColumnVisible('stock')" [style.order]="columnOrderIndex('stock')">
                          <ng-container *ngIf="product.stock !== null && product.stock !== undefined; else stockEmpty">
                            {{ product.stock }}
                          </ng-container>
                          <ng-template #stockEmpty>--</ng-template>
                        </td>
                        <!-- Product custom field columns (WB-7619) — read-only. -->
                        <td *ngFor="let key of visibleCustomFieldColumns" [style.order]="columnOrderIndex(key)">
                          <ng-container *ngIf="customFieldCell(product, key) as cell">
                            <ng-container [ngSwitch]="cell.kind">
                              <span *ngSwitchCase="'chips'" class="custom-field-chips">
                                <span class="custom-field-chip" *ngFor="let chip of cell.chips">{{ chip }}</span>
                              </span>
                              <a *ngSwitchCase="'link'" [href]="cell.href" target="_blank" rel="noopener noreferrer"
                                 class="custom-field-link" (click)="$event.stopPropagation()">{{ cell.text }}</a>
                              <span *ngSwitchDefault>{{ cell.text }}</span>
                            </ng-container>
                          </ng-container>
                        </td>
                      </tr>
                    </ng-template>

                    <ng-container *ngFor="let product of combinedLines; trackBy: trackByProduct">
                      <ng-container *ngIf="!product._isUnmatched">
                        <ng-container
                          *ngTemplateOutlet="standardProductRow; context: { $implicit: product }"
                        ></ng-container>
                      </ng-container>
                      <tr *ngIf="product._isUnmatched" class="row" [style.grid-template-columns]="gridTemplateColumns"
                          [class.row--active]="isRowActive(product)"
                          [attr.data-row-key]="rowKeyOf(product)"
                          (mouseenter)="onRowHover(product)" (mouseleave)="onRowHoverEnd()">
                        <td *ngIf="!viewMode">
                          <mat-checkbox
                            [checked]="isProductSelected(product)"
                            (change)="toggleProduct(product, $event.checked)"
                            (click)="$event.stopPropagation()"
                          ></mat-checkbox>
                        </td>
                        <ng-container *ngIf="product._matchedAsNew; else unmatchedProductDisplay">
                          <!-- Matched-as-new display: unmatched product was matched to a real product -->
                          <td class="product-name-col" *ngIf="isColumnVisible('product')" [style.order]="columnOrderIndex('product')">
                            <div class="product-name-column">
                              <div class="product_name">
                                <span [title]="product.name">
                                  {{ product.name }}
                                </span>
                                <div class="product-state">
                                  <span class="product-sku">{{product | productCode}}</span>
                                  <div class="tag new-matched-tag">{{ "common.new" | translate }}</div>
                                  <ui-svg-icon *ngIf="canRollBack(product)" (click)="rollBackProduct(product)" svgIcon="rollback" size="medium" style="cursor: pointer;"></ui-svg-icon>
                                </div>
                              </div>
                              <div class="product-icons" [class.product-icons-view-mode]="viewMode">
                                <ui-svg-icon
                                  class="status-icon manual-match-icon"
                                  [svgIcon]="'manual-match-icon'"
                                  [style.cursor]="'default'"
                                  [matTooltip]="'Manual'"
                                  matTooltipClass="product-status-tooltip"
                                  size="medium">
                                </ui-svg-icon>
                              </div>
                            </div>
                          </td>
                          <td *ngIf="isColumnVisible('notes')" [style.order]="columnOrderIndex('notes')">--</td>
                          <td *ngIf="isColumnVisible('brand')" [style.order]="columnOrderIndex('brand')">
                            <span class="truncate" [title]="product.brand_name || ''">{{ product.brand_name || '--' }}</span>
                          </td>
                          <td *ngIf="isColumnVisible('msrp')" [style.order]="columnOrderIndex('msrp')">--</td>
                          <td *ngIf="isColumnVisible('cost')" [style.order]="columnOrderIndex('cost')">--</td>
                          <!-- unit price (editable, same as matched rows) -->
                          <td class="price-cell" *ngIf="isColumnVisible('unit_price')" [style.order]="columnOrderIndex('unit_price')">
                            <div class="price-input-wrapper standard-input">
                              <input
                                type="number"
                                class="quantity-input"
                                min="0"
                                step="any"
                                [value]="priceInputValue(product.final_price)"
                                [disabled]="quotation?.all_lines_no_price"
                                (input)="onPriceInput($event, product)"
                                (click)="$event.stopPropagation()"
                              >
                              <ui-svg-icon *ngIf="product.price_type" tooltip=""
                                 [tooltipTemplate]="matchedPriceDetailsTpl"
                                 [offsetLeft]="200"
                                 [offsetTop]="-10"
                                 [maxWidth]="'350px'"
                                 svgIcon="info" size="medium" (click)="$event.stopPropagation()">
                              </ui-svg-icon>
                            </div>
                            <ng-template #matchedPriceDetailsTpl>
                              <app-price-details-tooltip
                                [product]="product"
                                [currencySymbol]="quotationCurrency?.symbol || ''"
                                [viewMode]="!!quotation?.all_lines_no_price"
                                (applyPrice)="onApplyPriceFromTooltip(product, $event)">
                              </app-price-details-tooltip>
                            </ng-template>
                          </td>
                          <!-- quantity (handling-unit aware, same as matched rows) -->
                          <td class="quantity-cell" *ngIf="isColumnVisible('quantity')" [style.order]="columnOrderIndex('quantity')">
                            <div class="quantity-badge standard-input">
                              <input
                                type="number"
                                class="quantity-input"
                                min="0"
                                step="any"
                                [value]="product.quantity_handling_unit ?? 0"
                                (input)="onHandlingUnitQtyInput($event, product)"
                              >
                              <mat-select
                                  class="hu-select"
                                  [class.hu-select-warning]="requiresHandlingUnitSelection(product)"
                                  panelClass="hu-select-panel"
                                  (selectionChange)="onHandlingUnitChange($event, product)"
                                  [value]="product.handling_unit_id"
                                  *ngIf="getHandlingUnits(product).length > 0"
                                  disableRipple
                              >
                                  <mat-select-trigger>
                                    <div class="hu-option-row" *ngIf="getSelectedHandlingUnit(product)">
                                        <span class="hu-option-name">{{ getSelectedHandlingUnit(product)?.name }}</span>
                                    </div>
                                  </mat-select-trigger>
                                  <mat-option *ngFor="let hu of getHandlingUnits(product)" [value]="hu.id">
                                      <div class="hu-option-row">
                                          <span class="hu-option-name">{{ hu.name }}</span>
                                          <span class="hu-option-quantity">{{ hu.quantity }} units</span>
                                      </div>
                                  </mat-option>
                                  <mat-option *appHasPermission="{ module: 'product', action: 'edit' }" class="hu-create-option" [value]="HU_CREATE_SENTINEL">
                                      <ui-svg-icon svgIcon="plus-cc" size="small"></ui-svg-icon>
                                      <span class="hu-create-label">{{ 'quotations_and_orders.side_by_side_panel.create_selling_unit' | translate }}</span>
                                  </mat-option>
                              </mat-select>
                              <ui-svg-icon tooltip="" [tooltipTemplate]="matchedHuTooltipTpl" [offsetLeft]="250" [offsetTop]="-10" [svgIcon]="requiresHandlingUnitSelection(product) ? 'alert-circle' : 'info'" size="medium" (click)="$event.stopPropagation()"></ui-svg-icon>
                              <ng-template #matchedHuTooltipTpl>
                                <app-qty-details-tooltip
                                  [item]="product"
                                  [selectedHu]="getSelectedHandlingUnit(product)">
                                </app-qty-details-tooltip>
                              </ng-template>
                            </div>
                          </td>
                          <td *ngIf="isColumnVisible('quantity_preferred_selling_unit')" [style.order]="columnOrderIndex('quantity_preferred_selling_unit')">
                            <ng-container *ngIf="product.quantity_preferred_selling_unit !== null && product.quantity_preferred_selling_unit !== undefined; else noPreferredSellingUnitB">
                              {{ product.quantity_preferred_selling_unit }} {{ product.quantity_preferred_selling_unit_name }}
                            </ng-container>
                            <ng-template #noPreferredSellingUnitB>--</ng-template>
                          </td>
                          <td *ngIf="isColumnVisible('quantity_units')" [style.order]="columnOrderIndex('quantity_units')">
                            {{ product.quantity }} {{ 'quotations_and_orders.side_by_side_panel.units_suffix' | translate }}
                          </td>
                          <!-- total (uses editable final price) -->
                          <td class="price-cell" [class.no-price]="product.price_type === 'none'" *ngIf="isColumnVisible('total_amount')" [style.order]="columnOrderIndex('total_amount')">
                            {{ product.price_type === 'none' ? ('quotations_and_orders.price_source_none' | translate) : (product.final_price ? ((product.final_price * (product.quantity || 0) | number: '1.2-2') + ' ' + (quotationCurrency?.symbol || '')) : '--') }}
                          </td>
                          <td *ngIf="isColumnVisible('profit_margin')" [style.order]="columnOrderIndex('profit_margin')">--</td>
                          <td *ngIf="isColumnVisible('delivery_date')" [style.order]="columnOrderIndex('delivery_date')">
                            <div class="standard-input has-icon">
                              <div class="input-container">
                                <input
                                    type="text"
                                    matInput
                                    [matDatepicker]="unmatchedPicker"
                                    class="standard-input-value"
                                    [placeholder]="'common.select_date' | translate"
                                    [value]="getDeliveryDateInputValue(product)"
                                    (dateChange)="onMatchedUnmatchedDeliveryDateChange($event, product)"
                                    (click)="unmatchedPicker.open()"
                                />
                                <button
                                    type="button"
                                    class="btn-icon"
                                    (click)="unmatchedPicker.open()"
                                    tabindex="-1"
                                    [attr.aria-label]="'quotations_and_orders.side_by_side_panel.open_calendar' | translate"
                                >
                                    <ui-svg-icon svgIcon="calendar-cc"></ui-svg-icon>
                                </button>
                                <mat-datepicker #unmatchedPicker></mat-datepicker>
                              </div>
                            </div>
                          </td>
                          <td *ngIf="isColumnVisible('stock')" [style.order]="columnOrderIndex('stock')">--</td>
                          <!-- Matched-as-new lines carry a real product, so custom field values apply (WB-7619). -->
                          <td *ngFor="let key of visibleCustomFieldColumns" [style.order]="columnOrderIndex(key)">
                            <ng-container *ngIf="customFieldCell(product, key) as cell">
                              <ng-container [ngSwitch]="cell.kind">
                                <span *ngSwitchCase="'chips'" class="custom-field-chips">
                                  <span class="custom-field-chip" *ngFor="let chip of cell.chips">{{ chip }}</span>
                                </span>
                                <a *ngSwitchCase="'link'" [href]="cell.href" target="_blank" rel="noopener noreferrer"
                                   class="custom-field-link" (click)="$event.stopPropagation()">{{ cell.text }}</a>
                                <span *ngSwitchDefault>{{ cell.text }}</span>
                              </ng-container>
                            </ng-container>
                          </td>
                        </ng-container>
                        <ng-template #unmatchedProductDisplay>
                          <!-- Original unmatched display -->
                          <td class="product-name-col" *ngIf="isColumnVisible('product')" [style.order]="columnOrderIndex('product')">
                            <div class="product-name-column">
                              <div class="product_name">
                                <span class="unmatched-product-name" [title]="product.ai_extracted_name">
                                  {{ product.ai_extracted_name }}
                                </span>
                              </div>
                              <div class="product-icons">
                                <ui-svg-icon class="unmatched-icon" svgIcon="unmatched-icon" style="cursor: pointer" (click)="!viewMode && openMatchSingleOrderLineProduct(product, $event, true); $event.stopPropagation()" size="medium" [matTooltip]="'Unmatched product'" matTooltipClass="product-status-tooltip"></ui-svg-icon>
                              </div>
                            </div>
                          </td>
                          <td *ngIf="isColumnVisible('notes')" [style.order]="columnOrderIndex('notes')">--</td>
                          <td *ngIf="isColumnVisible('brand')" [style.order]="columnOrderIndex('brand')">
                            <span class="truncate" title="--">--</span>
                          </td>
                          <td *ngIf="isColumnVisible('msrp')" [style.order]="columnOrderIndex('msrp')">--</td>
                          <td *ngIf="isColumnVisible('cost')" [style.order]="columnOrderIndex('cost')">--</td>
                          <td *ngIf="isColumnVisible('unit_price')" [style.order]="columnOrderIndex('unit_price')">--</td>
                          <td class="quantity-cell" *ngIf="isColumnVisible('quantity')" [style.order]="columnOrderIndex('quantity')">
                            {{product.quantity}}
                          </td>
                          <td *ngIf="isColumnVisible('quantity_preferred_selling_unit')" [style.order]="columnOrderIndex('quantity_preferred_selling_unit')">--</td>
                          <td *ngIf="isColumnVisible('quantity_units')" [style.order]="columnOrderIndex('quantity_units')">{{product.quantity}} {{ 'quotations_and_orders.side_by_side_panel.units_suffix' | translate }}</td>
                          <td *ngIf="isColumnVisible('total_amount')" [style.order]="columnOrderIndex('total_amount')">--</td>
                          <td *ngIf="isColumnVisible('profit_margin')" [style.order]="columnOrderIndex('profit_margin')">--</td>
                          <td *ngIf="isColumnVisible('delivery_date')" [style.order]="columnOrderIndex('delivery_date')">{{product.delivery_date | date: DELIVERY_DATE_FORMAT}}</td>
                          <td *ngIf="isColumnVisible('stock')" [style.order]="columnOrderIndex('stock')">--</td>
                          <!-- Unmatched lines have no product, so no custom field values (WB-7619). -->
                          <td *ngFor="let key of visibleCustomFieldColumns" [style.order]="columnOrderIndex(key)">--</td>
                        </ng-template>
                      </tr>
                    </ng-container>

                    <ng-container *ngIf="newProducts.length > 0">
                      <div class="newly-added-divider">
                        {{ 'quotations_and_orders.side_by_side_panel.newly_added' | translate }}
                      </div>
                      <ng-container *ngFor="let product of newProducts; trackBy: trackByProduct">
                        <ng-container
                          *ngTemplateOutlet="standardProductRow; context: { $implicit: product }"
                        ></ng-container>
                      </ng-container>
                    </ng-container>
                  </div>
                </tbody>
              </table>
          </div>
          <div class="lines-load-more" *ngIf="hasMoreLines || linesLoading">
            <mat-spinner *ngIf="linesLoading" diameter="20"></mat-spinner>
            <span *ngIf="autoLoadingLines" class="lines-load-more__hint">
              {{ 'quotations_and_orders.side_by_side_panel.loading_remaining_lines' | translate }}
            </span>
            <button *ngIf="hasMoreLines && !linesLoading && !autoLoadingLines" type="button" class="btn-secondary load-more-btn" (click)="loadMoreLines()">
              {{ 'common.load_more' | translate }}
            </button>
          </div>
          <div class="search-box-container" *ngIf="!viewMode && isAddProductMode">
            <div class="standard-input has-icon search-box">
              <div class="input-container">
                <input type="text"
                  [placeholder]="'quotations_and_orders.side_by_side_panel.search_by_name_or_reference' | translate"
                  [matAutocomplete]="auto"
                  (input)="onSearch($event)"
                  (keydown)="onAddProductKeydown($event)"
                  #addProductInput
                  #addProductAutoTrigger="matAutocompleteTrigger"
                />
                <mat-autocomplete #auto="matAutocomplete" [autoActiveFirstOption]="true" (optionSelected)="onAddProductOptionSelected($event)" (opened)="onAutocompleteOpened()" (closed)="onAutocompleteClosed()">
                  <mat-option class="product-option" *ngFor="let voltaProduct of products$ | async" [value]="voltaProduct">
                    <div class="product-details-block">
                        <div class="product-icon">
                            <img class="col-img" [src]="voltaProduct.image ? baseUrl + voltaProduct.image + '?thumbnail=1' : placeholder" alt="">
                        </div>
                        <div class="product-details">
                            <span class="product-name">{{voltaProduct.name}}</span>
                            <span class="product-sku">{{voltaProduct | productCode}}</span>
                        </div>
                    </div>
                  </mat-option>
                  <mat-option *ngIf="isSearchLoading" disabled class="search-loader-option">
                    <div class="search-loader">
                      <span class="search-spinner"></span>
                    </div>
                  </mat-option>
                </mat-autocomplete>
                <span class="btn-icon" style="cursor: default;">
                  <ui-svg-icon svgIcon="search_icon"></ui-svg-icon>
                </span>
              </div>
            </div>
            <ui-button type="danger" icon="trash-cc" (click)="cancelAddProduct()"></ui-button>
          </div>
          <!-- WB-7478: "Add product" moved out of here into the products footer. -->
        </div>


        <div class="order-source-tab details-tab" *ngIf="leftPanelActiveTab === 'details' && !companyLocationEditMode">
          <!-- ── Slots: rows whose value is richer than a string ── -->
          <ng-template #companySlot>
            <div class="entity-tag entity-tag--single-line" *ngIf="!isCompanyMissing(); else companyMissingSlot"
                 [matTooltip]="detailsDraft.partnerCompanyName || ''"
                 matTooltipClass="product-status-tooltip"
                 matTooltipPosition="above">
              <ui-icon name="Store" size="medium"></ui-icon>
              <span class="entity-tag__label">{{ detailsDraft.partnerCompanyName }}</span>
            </div>
            <ng-template #companyMissingSlot>
              <button type="button" class="entity-tag entity-tag--missing"
                      [disabled]="viewMode"
                      (click)="openCompanyLocationEditor('company')">
                <ui-icon name="TriangleAlert" size="medium"></ui-icon>
                <span class="entity-tag__label">{{ 'common.missing' | translate }}</span>
              </button>
            </ng-template>
          </ng-template>

          <ng-template #locationSlot>
            <span class="entity-tag-stack" *ngIf="!isLocationMissing(); else locationMissingSlot">
              <div class="entity-tag entity-tag--single-line"
                   *ngIf="isLocationDifferentCompany() && quotation?.location?.company_name"
                   [matTooltip]="quotation?.location?.company_name || ''"
                   matTooltipClass="product-status-tooltip"
                   matTooltipPosition="above">
                <ui-icon name="Store" size="medium"></ui-icon>
                <span class="entity-tag__label">{{ quotation?.location?.company_name }}</span>
              </div>
              <div class="entity-tag-row">
                <div class="entity-tag"
                     [matTooltip]="detailsDraft.partnerName || ''"
                     matTooltipClass="product-status-tooltip"
                     matTooltipPosition="above">
                  <ui-icon name="MapPin" size="medium"></ui-icon>
                  <span class="entity-tag__label">{{ detailsDraft.partnerName }}</span>
                </div>
                <span
                  *ngIf="quotation?.location?.provenance?.source === 'volta'"
                  class="volta-location-icon"
                  [matTooltip]="'customers.contacts_tab.volta_location_tooltip' | translate"
                  matTooltipClass="product-status-tooltip volta-location-tooltip"
                  matTooltipPosition="above"
                >
                  <img src="assets/icones/volta_smal_icon.svg" alt="Volta" width="14" height="14">
                </span>
                <span *ngIf="quotation?.location?.is_new" class="new-location-tag">
                  {{ 'companies_selection_dialog.new' | translate }}
                </span>
                <span
                  *ngIf="shippingAutofillSource() === 'single_location_fallback'"
                  class="autofill-badge"
                  [matTooltip]="'quotations_and_orders.side_by_side_panel.details.shipping_autofill.single_location' | translate"
                  matTooltipClass="product-status-tooltip"
                  matTooltipPosition="above"
                >
                  <ui-svg-icon svgIcon="ai-volta-icon" size="small"></ui-svg-icon>
                </span>
              </div>
            </span>
            <ng-template #locationMissingSlot>
              <div class="missing-information-container">
                <ng-container *ngIf="isSingleLocationSoftMatch(); else plainMissingSlot">
                  <div class="entity-tag-row soft-match-tags">
                    <div class="entity-tag"
                         [matTooltip]="suggestedShippingLocation()?.name || ''"
                         matTooltipClass="product-status-tooltip"
                         matTooltipPosition="above">
                      <ui-icon name="MapPin" size="medium"></ui-icon>
                      <span class="entity-tag__label">{{ suggestedShippingLocation()?.name }}</span>
                    </div>
                    <span class="soft-match-badge"
                      [matTooltip]="'quotations_and_orders.side_by_side_panel.details.shipping_autofill.single_location_soft_match' | translate"
                      matTooltipClass="product-status-tooltip"
                      matTooltipPosition="above"
                    >
                      <span>{{ 'quotations_and_orders.soft_match.suggestion' | translate }}</span>
                    </span>
                  </div>
                </ng-container>
                <ng-template #plainMissingSlot>
                  <button type="button" class="entity-tag entity-tag--missing"
                    [disabled]="viewMode"
                    (click)="openCompanyLocationEditor('location')"
                    [matTooltip]="shippingHintTooltip()"
                    matTooltipClass="product-status-tooltip"
                    matTooltipPosition="above"
                    [matTooltipDisabled]="!shippingHintTooltip()"
                  >
                    <ui-icon name="TriangleAlert" size="medium"></ui-icon>
                    <span class="entity-tag__label">{{ 'common.missing' | translate }}</span>
                  </button>
                </ng-template>
              </div>
            </ng-template>
          </ng-template>

          <ng-template #shippingAddressSlot>
            <ng-container *ngIf="isSingleLocationSoftMatch(); else shippingAddressValueTpl">
              <span>{{ suggestedShippingLocation()?.shipping_address || '--' }}</span>
            </ng-container>
            <!-- WB-7836 (review follow-up): the Shipping block's address is
                 the shipping location's own, not the Customer/billing
                 location's. -->
            <ng-template #shippingAddressValueTpl>
              <span>{{ detailsDraft.shippingAddress || quotation?.shipping_location?.shipping_address || '--' }}</span>
            </ng-template>
          </ng-template>

          <ng-template #mainContactSlot>
            <span class="entity-tag entity-tag--static"
                  [matTooltip]="quotation?.shipping_location?.contacts_information?.[0]?.contact_fname || ''"
                  matTooltipClass="product-status-tooltip"
                  matTooltipPosition="above">
              <ui-icon name="User" size="medium"></ui-icon>
              <span class="entity-tag__label">{{ quotation?.shipping_location?.contacts_information?.[0]?.contact_fname }}</span>
            </span>
          </ng-template>

          <ng-template #contactEmailSlot>
            <ng-container *ngIf="quotation?.shipping_location?.contacts_information?.[0]?.email as email">
              <a class="contact-email-link" [href]="'mailto:' + email">{{ email || '--' }}</a>
            </ng-container>
          </ng-template>

          <ng-template #freeShippingSlot>
            <span class="entity-tag entity-tag--static">
              <img src="assets/icones/gift.svg" alt=""/>
              Free
            </span>
          </ng-template>

          <!-- One tag per code: a location carries several types, and a type
               several values. -->
          <ng-template #taxCodesSlot>
            <span class="tax-code-list" *ngIf="taxCodeTags.length; else noTaxCodes">
              <span class="entity-tag entity-tag--static tax-code-tag" *ngFor="let code of taxCodeTags">
                <span class="tax-code-tag__type">{{ code.type }}</span>
                <ui-copyable [value]="code.value" [label]="code.type">{{ code.value }}</ui-copyable>
              </span>
            </span>
            <ng-template #noTaxCodes><span>--</span></ng-template>
          </ng-template>

          <!-- Sits directly under the billing-company row, not at the end of the
               block: the sentence names that row's value. -->
          <ng-template #companyAlertSlot>
            <div class="details-row" *ngIf="isCompanyMissing() && extractedCompanyRaw() as extractedCompany">
              <div class="ai-suggestion-card">
                <div class="ai-suggestion-card__content">
                  <ui-svg-icon svgIcon="ai-volta-icon" size="medium"></ui-svg-icon>
                  <span>
                    <ui-copyable [value]="extractedCompany"><strong>{{ extractedCompany }}</strong></ui-copyable>
                    {{ 'quotations_and_orders.soft_match.company_not_in_system' | translate }}
                  </span>
                </div>
              </div>
            </div>
          </ng-template>

          <!-- ── Customer ────────────────────────────────────────────────── -->
          <ui-detail-field-list
            heading="quotations_and_orders.customer"
            [fields]="customerFields"
            [editable]="!viewMode"
            [slots]="{ company: companySlot }"
            [afterSlots]="{ company: companyAlertSlot }"
            (fieldEdit)="onDetailFieldEdit($event)"
            (fieldValueChange)="onDetailFieldValueChange($event)"
          ></ui-detail-field-list>

          <!-- ── Shipping ────────────────────────────────────────────────── -->
          <hr class="details-divider"/>

          <ui-detail-field-list
            heading="companies.shipping"
            [fields]="shippingFields"
            [editable]="!viewMode"
            [slots]="{
              location: locationSlot,
              shipping_address: shippingAddressSlot,
              main_contact: mainContactSlot,
              contact_email: contactEmailSlot,
              free_shipping: freeShippingSlot,
              tax_codes: taxCodesSlot
            }"
            (fieldEdit)="onDetailFieldEdit($event)"
            (fieldValueChange)="onDetailFieldValueChange($event)"
          >
            <div class="details-row" *ngIf="isLocationDifferentCompany()">
              <span class="details-value company-diff-alert">
                <img src="assets/icones/alert-circle.svg" alt="">
                <span class="company-diff-alert-text">{{ 'companies_selection_dialog.billing_shipping_different' | translate }}</span>
              </span>
            </div>

            <div class="details-row" *ngIf="!viewMode && isSingleLocationSoftMatch()">
              <div class="ai-suggestion-card">
                <div class="ai-suggestion-card__content">
                  <ui-svg-icon svgIcon="ai-volta-icon" size="medium"></ui-svg-icon>
                  <span>{{ 'quotations_and_orders.soft_match.accept_suggested_location' | translate }}</span>
                </div>
                <button type="button" class="ai-suggestion-card__btn accept"
                  (click)="confirmSuggestedLocation()"
                  [attr.aria-label]="'common.accept' | translate">
                  <ui-svg-icon svgIcon="check-cc" size="medium"></ui-svg-icon>
                </button>
                <button type="button" class="ai-suggestion-card__btn discard"
                  (click)="discardSuggestedLocation()"
                  [attr.aria-label]="'common.discard' | translate">
                  <ui-svg-icon svgIcon="close-cc" size="medium"></ui-svg-icon>
                </button>
              </div>
            </div>

            <div class="details-row" *ngIf="!viewMode && isLocationMissing() && extractedShippingRaw() as rawShippingAddress">
              <div class="ai-suggestion-card">
                <div class="ai-suggestion-card__content">
                  <ui-svg-icon svgIcon="ai-volta-icon" size="medium"></ui-svg-icon>
                  <span><strong>{{ rawShippingAddress }}</strong> {{ 'quotations_and_orders.soft_match.address_not_in_system' | translate }}</span>
                </div>
                <ng-container *ngIf="featureFlags.allowCreateShippingAddress">
                  <button type="button" class="ai-suggestion-card__btn add"
                    (click)="addNewLocation()"
                    [attr.aria-label]="'common.add' | translate">
                    <ui-svg-icon svgIcon="add_icon" size="medium"></ui-svg-icon>
                  </button>
                </ng-container>
              </div>
            </div>
          </ui-detail-field-list>

          <!-- ── Order references ────────────────────────────────────────── -->
          <hr class="details-divider"/>

          <ui-detail-field-list
            #orderReferencesList
            heading="quotations_and_orders.order_references"
            [fields]="orderReferenceFields"
            [editable]="!viewMode"
            (fieldValueChange)="onDetailFieldValueChange($event)"
          >
            <div *ngIf="extractedNotes?.extracted?.length" class="details-row flex-column">
              <span class="details-label">{{ 'quotations_and_orders.extracted_notes' | translate }}</span>
              <app-extracted-notes-editor
                class="full-width"
                [notes]="extractedNotes"
                [editable]="!viewMode"
                (notesSave)="onExtractedNotesSave($event)"
              ></app-extracted-notes-editor>
            </div>
          </ui-detail-field-list>

          <!-- ── Custom fields ───────────────────────────────────────────── -->
          <hr class="details-divider"/>

          <ui-detail-field-list
            heading="custom_fields.custom_fields"
            [fields]="orderCustomFieldRows"
            [editable]="!viewMode"
            (fieldValueChange)="onDetailFieldValueChange($event)"
          >
            <ng-container detailFieldListActions *appHasPermission="{ module: 'order', action: 'edit' }">
              <button
                type="button"
                class="details-edit-button"
                *ngIf="!viewMode && hasDialogOnlyCustomFields()"
                (click)="editCustomFields()"
                [attr.aria-label]="'common.edit' | translate"
              >
                <img src="assets/icones/edit_icon.svg" alt="">
              </button>
            </ng-container>
          </ui-detail-field-list>

          <hr class="details-divider"/>

          <!-- ── Details (never configurable) ────────────────────────────── -->
          <ui-detail-field-list heading="quotations_and_orders.details">
            <div class="details-row">
              <span class="details-label">{{ "companies.order_channel" | translate }}</span>
              <span class="details-value">
                <span class="details-badge order-channel-badge" [class]="quotation?.channel === 'BO portal' || quotation?.channel === 'prompt' ? 'backoffice' : 'email'">
                  <img *ngIf="quotation?.channel === 'email'" src="assets/icones/email.svg" alt="">
                  <img *ngIf="quotation?.channel === 'B2B portal' || quotation?.channel === 'Agent portal'" src="assets/icones/volta_smal_icon.svg" alt="">
                  <!-- The `_tr` variant is a hardcoded *white* glyph for dark
                       backgrounds; this badge is light, so use the purple one
                       rather than filter-recolouring it. -->
                  <img *ngIf="quotation?.channel === 'BO portal' || quotation?.channel === 'prompt'" src="assets/icones/volta_smal_icon.svg" alt="">
                  <span *ngIf="quotation?.channel === 'BO portal' || quotation?.channel === 'prompt'">{{ 'quotations_and_orders.backoffice' | translate }}</span>
                  <span *ngIf="quotation?.channel === 'B2B portal'">{{ 'quotations_and_orders.b2b_portal' | translate }}</span>
                  <span *ngIf="quotation?.channel === 'Agent portal'">{{ 'quotations_and_orders.agent_portal' | translate }}</span>
                  <span *ngIf="quotation?.channel === 'email'">{{ 'quotations_and_orders.email' | translate }}</span>
                </span>
              </span>
            </div>

            <div class="details-row ownership-inline-row">
              <span class="details-label">{{ 'quotations_and_orders.ownership.created_by' | translate }}</span>
              <span class="details-value ownership-inline-value">
                <ng-container *ngIf="quotation?.order_creator as cr; else noCreatorTpl">
                  <span class="own-avatar-circle" [style.background]="getUserColor(cr)" aria-hidden="true">{{ userChipInitials(cr.name) }}</span>
                  <span class="own-display-name">{{ cr.name }}</span>
                </ng-container>
                <ng-template #noCreatorTpl><span class="own-dash">—</span></ng-template>
              </span>
            </div>

            <div class="details-row ownership-inline-row">
              <span class="details-label">{{ 'quotations_and_orders.ownership.owner' | translate }}</span>
              <span class="details-value ownership-inline-value ownership-owner-with-action">
                <!-- Owner is drafted here, so the pencil opens this panel's own
                     autocomplete rather than the detail page's dialog; the
                     resting state is deliberately identical to it. -->
                <ng-container *ngIf="!ownerEditMode; else ownerSearchTpl">
                  <ng-container *ngIf="detailsDraft.ownerId; else noOwnerTpl">
                    <span class="own-avatar-circle" [style.background]="getUserColor({ id: detailsDraft.ownerId, name: detailsDraft.ownerName || '' })" aria-hidden="true">{{ userChipInitials(detailsDraft.ownerName) }}</span>
                    <span class="own-display-name">{{ detailsDraft.ownerName }}</span>
                  </ng-container>
                  <ng-template #noOwnerTpl><span class="own-dash">—</span></ng-template>
                  <div class="owner-actions" *ngIf="!viewMode">
                    <button
                      type="button"
                      class="details-edit-button"
                      (click)="enterOwnerEdit(); $event.stopPropagation()"
                      [attr.aria-label]="'quotations_and_orders.ownership.change_owner' | translate"
                    >
                      <img src="assets/icones/edit_icon.svg" alt="">
                    </button>
                  </div>
                </ng-container>
                <ng-template #ownerSearchTpl>
                  <div class="owner-search-wrapper">
                    <div class="standard-input">
                      <input
                        type="text"
                        class="standard-input-value"
                        [formControl]="ownerSearchControl"
                        [matAutocomplete]="ownerAuto"
                        (input)="onOwnerSearchInput(ownerSearchControl.value)"
                        (keydown.escape)="$event.stopPropagation(); cancelOwnerEdit()"
                        (blur)="onOwnerSearchBlur()"
                        placeholder="search by name"
                        autocomplete="off"
                        #ownerSearchInput
                      />
                      <mat-spinner *ngIf="ownerSearchLoading" diameter="16" class="owner-spinner"></mat-spinner>
                    </div>
                    <mat-autocomplete #ownerAuto="matAutocomplete" (optionSelected)="onOwnerSelected($event)">
                      <mat-option *ngFor="let u of ownerOptions" [value]="u">
                        <span class="owner-opt-row">
                          <img src="assets/icones/person.svg" width="14" height="14" alt="" />
                          <span>{{ u.name }}</span>
                        </span>
                      </mat-option>
                    </mat-autocomplete>
                  </div>
                </ng-template>
              </span>
            </div>

            <div class="details-row">
              <span class="details-label">{{ "companies.creation_date" | translate }}</span>
              <span class="details-value">{{ quotation?.date_order ? (quotation?.date_order | adaptToUserUtcOffset | date: DATE_FORMAT) : '--' }}</span>
            </div>

            <!-- Split provenance, same rows and popover as the detail page. -->
            <div class="details-row" *ngIf="quotation?.segregation_info as info">
              <span class="details-label">{{ 'quotations_and_orders.segregation.split_from' | translate }}</span>
              <span class="details-value seg-inline-value">
                <a class="entity-tag" [routerLink]="['/quotations', info.source_id]">
                  <ui-icon name="Split" size="medium"></ui-icon>
                  <span class="entity-tag__label">{{ info.source_name }}</span>
                </a>
                <span class="seg-info-trigger"
                      [matTooltip]="segregationRuleSummary(info)"
                      matTooltipClass="product-status-tooltip seg-info-tooltip"
                      matTooltipPosition="above">
                  <ui-icon name="Info" size="medium"></ui-icon>
                </span>
              </span>
            </div>

            <div class="details-row" *ngIf="quotation?.segregation_splits?.length" [style.alignItems]="'start'">
              <span class="details-label">{{ 'quotations_and_orders.segregation.split_into' | translate }}</span>
              <span class="details-value seg-inline-list">
                <span class="seg-inline-row" *ngFor="let split of quotation?.segregation_splits">
                  <a class="entity-tag" [routerLink]="['/quotations', split.id]">
                    <ui-icon name="Split" size="medium"></ui-icon>
                    <span class="entity-tag__label">{{ split.name }}</span>
                  </a>
                  <span class="seg-info-trigger"
                        [matTooltip]="segregationRuleSummary(split)"
                        matTooltipClass="product-status-tooltip seg-info-tooltip"
                        matTooltipPosition="above">
                    <ui-icon name="Info" size="medium"></ui-icon>
                  </span>
                </span>
              </span>
            </div>
          </ui-detail-field-list>
        </div>
      </div>
      <!--
        WB-7478: "Add product" now lives here as a secondary button on the left,
        with the pricing info on the right. Applies to both origins.
      -->
      <div *ngIf="leftPanelActiveTab === 'products' && !productMatchEditMode" class="tabs footer">
        <div class="footer-actions">
          <ui-button
            *ngIf="!viewMode"
            class="add_product_button"
            type="secondary"
            [label]="'inventory.add_product' | translate"
            icon="plus-cc"
            iconPosition="start"
            [disabled]="isAddProductMode"
            (click)="startAddProduct()">
          </ui-button>
        </div>
        <div class="total">
          {{ 'quotations_and_orders.side_by_side_panel.total' | translate }}:
        </div>
        <div class="total-value" [class.no-price]="quotation?.all_lines_no_price">
          <span *ngIf="quotation?.all_lines_no_price; else totalAmountValue">{{ 'quotations_and_orders.price_source_none' | translate }}</span>
          <ng-template #totalAmountValue>
            <span>{{ (quotation?.priced_lines_total ?? 0).toFixed(2) + ' ' + this.quotation?.currency?.symbol! }}</span>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Right Panel -->
    <div class="right-panel">
      <div class="tabs order-source-tabs">
        <div
          class="tab"
          role="tab"
          [attr.aria-selected]="rightPanelActiveTab === 'order-source'"
          [class.active]="rightPanelActiveTab === 'order-source'"
          (click)="rightPanelActiveTab = 'order-source'"
          data-testid="order-source-tab"
        >
          {{ 'quotations_and_orders.side_by_side_panel.order_source' | translate }}
        </div>

      </div>
      <div class="tab-content-wrapper">
        <div class="email-content-wrapper" *ngIf="rightPanelActiveTab === 'order-source' && quotationSourceContent">
          <div class="order-content-tab">
            <div class="content-header">
              <div class="content-header-title">
                <span>
                  {{ 'quotations_and_orders.side_by_side_panel.content' | translate }}
                </span>
                <span class="details-badge order-channel-badge email">
                  <img [src]="quotation?.channel === 'email' ? 'assets/icones/email.svg' : 'assets/icones/edit.svg'" alt="">
                  <span>{{quotation?.channel}}</span>
                </span>
              </div>
              <ui-button type="secondary" icon="arrow" iconSize="large" [rotated]="contentExpanded" (click)="toggleContentExpanded()"></ui-button>
            </div>
            <div
              *ngIf="quotationSourceContent && quotationSourceContent.email"
              class="email__content"
              [@expandCollapse]="contentExpanded ? 'expanded' : 'collapsed'"
              (@expandCollapse.done)="onContentExpandDone()"
            >
              <div class="customer-section">
                  <div *ngIf="quotation?.channel === 'email' && quotationSourceContent.email.subject" class="details-row">
                    <span class="details-label">{{ 'quotations_and_orders.side_by_side_panel.subject' | translate }}</span>
                    <span class="details-value">
                        {{quotationSourceContent.email.subject}}
                    </span>
                  </div>
                  <div *ngIf="quotation?.channel === 'email' && quotationSourceContent.email.from" class="details-row">
                      <span class="details-label">{{"common.from" | translate}}</span>
                      <span class="details-value">
                          {{quotationSourceContent.email.from}}
                      </span>
                  </div>
                   <div *ngIf="quotation?.channel === 'prompt' && quotationSourceContent.ai_details.created_by" class="details-row">
                      <span class="details-label">{{"common.by" | translate}}</span>
                      <span class="details-value">
                          {{quotationSourceContent.ai_details.created_by}}
                      </span>
                  </div>
                  <div *ngIf="quotationSourceContent.ai_details && quotationSourceContent.ai_details.created_at" class="details-row">
                      <span class="details-label">{{"common.date" | translate}}</span>
                      <span class="details-value">
                          {{quotationSourceContent.ai_details.created_at | adaptToUserUtcOffset | date: DATE_FORMAT}}
                      </span>
                  </div>
              </div>

              <hr class="email__divider">

              <div *ngIf="quotationSourceContent.email.content" class="email__message" [innerHTML]="quotationSourceContent.email.content"
                   [appEmailBodyHighlight]="textAnchorRegions"
                   [activeAnchorKey]="activeTextAnchorKey"
                   [anchorLocked]="!!lockedLineKey"
                   (anchorHover)="onBodyAnchorHover($event)">
              </div>
            </div>
          </div>
          <ng-container *ngIf="quotationSourceContent.email && quotationSourceContent.email.attachments && quotationSourceContent.email.attachments.length > 0">
              <div class="email__field">
                <div class="attachments-container">
                    <div class="attachment" *ngFor="let attachment of quotationSourceContent.email.attachments"
                        (click)="onAttachmentClick(attachment)"
                        [class.attachment--selected]="isAttachmentSelected(attachment)"
                        [title]="getAttachmentName(attachment)">
                        <img src="assets/icones/attachment.svg" alt="">
                        <span>
                            {{
                              (getAttachmentName(attachment) || '').length > 20
                                ? (getAttachmentName(attachment) || '' | slice:0:17) + '...'
                                : (getAttachmentName(attachment) || '')
                            }}
                          </span>
                    </div>
                </div>
              </div>
            <div class="attachments-section">
              <div class="attachment_preview" *ngIf="currentAttachment.length > 0">
                  <app-files-previewer
                      [data]="currentAttachment"
                      [highlight]="previewHighlight"
                      [lineRegions]="lineRegions"
                      [cellHighlight]="previewCellHighlight"
                      [cellRegions]="cellRegions"
                      (lineHover)="onDocLineHover($event)"
                  >
                  </app-files-previewer>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </div>
</div>
